import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { inject, injectable, skipAutoDispose } from 'src/features/ioc';
import { instrumentToString } from 'src/lib/instrumentUtils';
import type { ActiveUserModel } from 'src/models/ActiveUserModel';
import type { TableModel } from 'src/models/TableModel';
import { getTickersForSymbolQuery } from 'src/queries/table';
import type { MarketData, MarketDataService, MarketDataSubscription } from 'src/services/MarketDataService';
import type { ReactBindings } from 'src/types/bindings';

const MARKET_DATA_ROLES = {
    equity: 'market-data::equity',
    option: 'market-data::options',
} as const;

class SubscriptionEntry {
    readonly tableModel: TableModel;
    readonly marketData: ReadonlySignal<MarketData>;
    readonly isConnected: ReadonlySignal<boolean>;

    readonly #subscribers = new Set<string>();
    readonly #disposables: DisposableStack;
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    readonly #onDestroy: () => void;
    readonly #retentionDurationMs: number;
    #retentionTimer?: ReturnType<typeof setTimeout>;

    constructor(
        instrumentKey: string,
        tableModel: ReactBindings['TableModel'],
        logger: ReturnType<ReactBindings['Logger']['getSubLogger']>,
        retentionDurationMs: number,
        onDestroy: () => void,
    ) {
        using stack = new DisposableStack();

        this.#log = logger;
        this.#onDestroy = onDestroy;
        this.#retentionDurationMs = retentionDurationMs;

        // Create table model
        this.tableModel = tableModel;

        // Initialize table model
        this.tableModel.init({
            queryOptions: getTickersForSymbolQuery(instrumentKey),
            initBounds: { firstRow: 0, windowSize: 1 },
            initThrottle: 1,
            devtoolsName: 'MarketDataService',
        });

        stack.defer(() => this.tableModel.destroy());

        // Market data signal
        const marketDataSignal = signal<MarketData>({
            bid: undefined,
            ask: undefined,
            last: undefined,
        });

        this.marketData = marketDataSignal;
        this.isConnected = computed(() => this.tableModel.connectionStatus.value === 'connected');

        // Update market data from table
        stack.defer(
            effect(() => {
                if (!this.isConnected.value) return;

                const updateData = this.tableModel.updateSignal.value?.update;
                if (!updateData) return;

                const columns = this.tableModel.columnsSignal.value;
                const columnBid = columns.find((col) => col.matchSlug === Slugs.bid);
                const columnAsk = columns.find((col) => col.matchSlug === Slugs.ask);
                const columnLast = columns.find((col) => col.matchSlug === Slugs.last);

                marketDataSignal.value = {
                    bid: columnBid ? (updateData[columnBid.id] as number | undefined) : undefined,
                    ask: columnAsk ? (updateData[columnAsk.id] as number | undefined) : undefined,
                    last: columnLast ? (updateData[columnLast.id] as number | undefined) : undefined,
                };
            }),
        );

        this.#disposables = stack.move();
    }

    addSubscriber(subscriberId: string): void {
        if (TRACE) {
            this.#log.trace('Adding subscriber', { subscriberId });
        }

        this.#subscribers.add(subscriberId);

        // Clear retention timer if it was running
        if (this.#retentionTimer) {
            clearTimeout(this.#retentionTimer);
            this.#retentionTimer = undefined;
        }
    }

    removeSubscriber(subscriberId: string): void {
        if (TRACE) {
            this.#log.trace('Removing subscriber', { subscriberId });
        }

        this.#subscribers.delete(subscriberId);

        // Start retention timer if no more subscribers
        if (this.#subscribers.size === 0) {
            this.#retentionTimer = setTimeout(() => {
                if (this.#subscribers.size === 0) {
                    this.#onDestroy();
                }
            }, this.#retentionDurationMs);
        }
    }

    get subscriberCount(): number {
        return this.#subscribers.size;
    }

    [Symbol.dispose](): void {
        if (TRACE) {
            this.#log.trace('Disposing subscription entry');
        }

        if (this.#retentionTimer) {
            clearTimeout(this.#retentionTimer);
        }
        this.#disposables.dispose();
    }
}

class SubscriptionManager {
    readonly #entries = new Map<string, SubscriptionEntry>();
    readonly #tableModelProvider: ReactBindings['TableModelProvider'];
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    readonly #retentionDurationMs: number;

    constructor(
        tableModelProvider: ReactBindings['TableModelProvider'],
        logger: ReturnType<ReactBindings['Logger']['getSubLogger']>,
        retentionDurationMs: number,
    ) {
        this.#tableModelProvider = tableModelProvider;
        this.#log = logger;
        this.#retentionDurationMs = retentionDurationMs;
    }

    /**
     * Get existing entry or create a new one.
     */
    async getOrCreate(instrumentKey: string): Promise<SubscriptionEntry> {
        let entry = this.#entries.get(instrumentKey);

        if (!entry) {
            if (TRACE) {
                this.#log.trace('Creating new subscription', { instrumentKey });
            }

            const tableModel = await this.#tableModelProvider();
            entry = new SubscriptionEntry(instrumentKey, tableModel, this.#log, this.#retentionDurationMs, () => {
                if (TRACE) {
                    this.#log.trace('Destroying cached subscription', { instrumentKey });
                }
                this.delete(instrumentKey);
            });
            this.#entries.set(instrumentKey, entry);
        }

        return entry;
    }

    /**
     * Add a subscriber to an entry and return a disposable subscription.
     */
    addSubscriber(
        key: string,
        subscriberId: string,
    ): {
        tableModel: TableModel;
        marketData: ReadonlySignal<MarketData>;
        isConnected: ReadonlySignal<boolean>;
    } & Disposable {
        const entry = this.#entries.get(key);
        if (!entry) {
            throw new Error(`No entry found for key: ${String(key)}`);
        }

        entry.addSubscriber(subscriberId);

        return {
            tableModel: entry.tableModel,
            marketData: entry.marketData,
            isConnected: entry.isConnected,
            [Symbol.dispose]: () => {
                const currentEntry = this.#entries.get(key);
                if (currentEntry) {
                    currentEntry.removeSubscriber(subscriberId);
                }
            },
        };
    }

    /**
     * Delete an entry by key and dispose it.
     */
    delete(key: string): void {
        const entry = this.#entries.get(key);
        if (entry) {
            entry[Symbol.dispose]();
            this.#entries.delete(key);
        }
    }

    [Symbol.dispose](): void {
        for (const entry of this.#entries.values()) {
            entry[Symbol.dispose]();
        }
        this.#entries.clear();
    }
}

@injectable()
export class MarketDataServiceImpl implements MarketDataService {
    readonly [skipAutoDispose] = true;
    readonly #subscriptionManager: SubscriptionManager;
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    readonly #activeUserModel: ActiveUserModel;

    constructor(
        @inject('Logger') logger: ReactBindings['Logger'],
        @inject('TableModelProvider') tableModelProvider: ReactBindings['TableModelProvider'],
        @inject('ActiveUserModel') activeUserModel: ActiveUserModel,
    ) {
        this.#log = logger.getSubLogger({ name: 'MarketDataService' });
        this.#activeUserModel = activeUserModel;

        const retentionDurationMs = 2 * 60 * 1000; // 2 minutes
        this.#subscriptionManager = new SubscriptionManager(tableModelProvider, this.#log, retentionDurationMs);

        if (TRACE) {
            this.#log.trace('MarketDataService initialized');
        }
    }

    #hasPermission(instrumentType: 'equity' | 'option'): boolean {
        if (this.#activeUserModel.isSuperUserModeEnabled.value) {
            return true;
        }
        return this.#activeUserModel.userRoles.value.includes(MARKET_DATA_ROLES[instrumentType]);
    }

    async subscribe(instrument: Instrument): Promise<MarketDataSubscription | null> {
        if (!this.#hasPermission(instrument.type)) {
            if (TRACE) {
                this.#log.trace('Market data permission denied', { instrumentType: instrument.type });
            }
            return null;
        }

        const instrumentKey = this.#getInstrumentKey(instrument);

        // Get or create subscription entry
        const entry = await this.#subscriptionManager.getOrCreate(instrumentKey);

        // Track this subscriber and return subscription
        const subscriberId = crypto.randomUUID();
        const subscription = this.#subscriptionManager.addSubscriber(instrumentKey, subscriberId);

        if (TRACE) {
            this.#log.trace('Subscribed', { instrumentKey, subscriberCount: entry.subscriberCount });
        }

        return subscription;
    }

    async once(instrument: Instrument, options?: { signal: AbortSignal }): Promise<MarketData> {
        if (!this.#hasPermission(instrument.type)) {
            if (TRACE) {
                this.#log.trace('Market data permission denied for once()', { instrumentType: instrument.type });
            }
            return { bid: undefined, ask: undefined, last: undefined };
        }

        using stack = new DisposableStack();

        // Permission already checked above, so subscribe() will not return null
        const subscription = stack.use((await this.subscribe(instrument))!);

        const timeoutPromise = new Promise<never>((_resolve, reject) => {
            stack.adopt(
                setTimeout(() => {
                    reject(new Error(`Timeout waiting for market data for ${this.#getInstrumentKey(instrument)}`));
                }, 30000),
                clearTimeout,
            );
        });

        const marketDataPromise = new Promise<MarketData>((resolve) => {
            stack.defer(
                effect(() => {
                    const isConnected = subscription.isConnected.value;
                    const marketData = subscription.marketData.value;

                    if (
                        isConnected &&
                        (marketData.bid !== undefined || marketData.ask !== undefined || marketData.last !== undefined)
                    ) {
                        resolve(marketData);
                    }
                }),
            );
        });

        const promises: Promise<MarketData>[] = [marketDataPromise, timeoutPromise];

        if (options?.signal) {
            const signal = options.signal;

            const abortPromise = new Promise<never>((_resolve, reject) => {
                if (signal.aborted) {
                    reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
                    return;
                }

                const onAbort = () => {
                    reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
                };

                signal.addEventListener('abort', onAbort);
                stack.defer(() => signal.removeEventListener('abort', onAbort));
            });

            promises.push(abortPromise);
        }

        return await Promise.race(promises);
    }

    #getInstrumentKey(instrument: Instrument): string {
        return instrumentToString(instrument);
    }

    [Symbol.dispose](): void {
        this.#subscriptionManager[Symbol.dispose]();

        if (TRACE) {
            this.#log.trace('MarketDataService disposed');
        }
    }
}
